<script>
  import Icon from "@iconify/svelte";
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import { t } from "../lib/i18n.svelte.js";
  import { getUser, logout } from "../lib/stores/auth.svelte.js";
  import { api, getToken } from "../lib/api.svelte.js";
  import LanguageSwitcher from "./LanguageSwitcher.svelte";

  let mobileOpen = $state(false);
  let healthy = $state(null);
  let paused = $state(false);
  let user = $derived(getUser());
  let pollingTimer = null;
  let currentPath = $state(getPath());

  const navLinks = [
    { href: "/sources", label: () => t("nav.sources"), icon: "lucide:package" },
    { href: "/repos", label: () => t("nav.repos"), icon: "lucide:refresh-cw" },
    { href: "/packages", label: () => t("nav.packages"), icon: "lucide:book-open" },
    { href: "/products", label: () => t("nav.products"), icon: "lucide:box" },
    { href: "/consumers", label: () => t("nav.consumers"), icon: "lucide:users" },
    { href: "/storage", label: () => t("nav.storage"), icon: "lucide:folder-open" },
    { href: "/settings", label: () => t("nav.settings"), icon: "lucide:settings" },
    { href: "/runtime", label: () => t("nav.runtime"), icon: "lucide:terminal-square" },
  ];

  function getPath() {
    const hash = window.location.hash.slice(1) || "/";
    return hash.split("?")[0] || "/";
  }

  function isActive(href) {
    return currentPath === href || currentPath.startsWith(href + "/");
  }

  function go(href) {
    currentPath = href;
    push(href);
    mobileOpen = false;
  }

  async function handleLogout() {
    await logout();
    push("/login");
  }

  async function pingHealth() {
    try {
      await api.get("/up");
      healthy = true;
    } catch {
      healthy = false;
    }
  }

  function refreshStatus() {
    pingHealth();
  }

  function togglePause() {
    paused = !paused;
    if (!paused) refreshStatus();
  }

  onMount(() => {
    pingHealth();
    pollingTimer = setInterval(() => {
      if (paused) return;
      if (document.visibilityState === "hidden") return;
      pingHealth();
    }, 10000);

    const onHash = () => {
      currentPath = getPath();
    };
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
    };

    window.addEventListener("hashchange", onHash);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(pollingTimer);
      window.removeEventListener("hashchange", onHash);
      document.removeEventListener("visibilitychange", onVis);
    };
  });
</script>

<div class="pt-3 px-3 sm:px-6">
  <header class="sticky top-2 z-50 ui-topbar">
    <div class="w-full px-3 py-2 sm:px-4 sm:py-3">
      <div class="flex items-center justify-between gap-3">
        <button type="button" class="flex items-center gap-3 text-left min-w-0" onclick={() => go("/dashboard")}>
          <span class="ui-logo text-semibold" aria-hidden="true">
            <Icon icon="game-icons:jet-fighter" width="18" height="18" />
          </span>
          <span class="flex flex-col leading-tight">
            <span class="text-base sm:text-lg font-semibold tracking-wide text-white">{t("app.name")}</span>
            <span class="hidden sm:block text-xs text-zinc-400">{t("app.tagline")}</span>
          </span>
        </button>

        <div class="hidden md:flex flex-wrap items-center justify-end gap-3">
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 h-10"
              onclick={refreshStatus}
              title={t("common.refresh")}
            >
              <span class={"h-2.5 w-2.5 rounded-full " + (healthy === null ? "bg-zinc-500" : healthy ? "bg-emerald-400" : "bg-rose-400")}></span>
              <span class="text-sm text-zinc-200/90">{healthy === null ? t("nav.checking") : healthy ? t("common.connected") : t("common.disconnected")}</span>
            </button>

            <button
              type="button"
              class={`inline-flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all select-none text-zinc-100/90 hover:text-white ${paused ? "opacity-80" : ""}`}
              title={paused ? t("common.play") : t("common.pause")}
              aria-label={paused ? t("common.play") : t("common.pause")}
              onclick={togglePause}
            >
              <Icon icon={paused ? "lucide:play" : "lucide:pause"} width="18" height="18" />
            </button>

            <button
              type="button"
              class="inline-flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all select-none text-zinc-100/90 hover:text-white"
              title={t("common.refresh")}
              aria-label={t("common.refresh")}
              onclick={refreshStatus}
            >
              <Icon icon="lucide:refresh-cw" width="18" height="18" />
            </button>

            <div class="w-px h-6 bg-white/10 mx-2"></div>
          </div>

          <nav class="topnav topnav--nowrap">
            {#each navLinks as link}
              <button
                type="button"
                class="topnav__tab"
                class:is-active={isActive(link.href)}
                onclick={() => go(link.href)}
                title={link.label()}
                aria-label={link.label()}
              >
                <Icon icon={link.icon} width="16" height="16" />
                <span>{link.label()}</span>
              </button>
            {/each}
          </nav>

          <div class="flex items-center gap-2">
            <div class="w-px h-6 bg-white/10 mx-2"></div>
            <LanguageSwitcher />
            {#if user}
              <button
                type="button"
                class="inline-flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all select-none text-zinc-100/90 hover:text-white"
                title={t("nav.logout")}
                aria-label={t("nav.logout")}
                onclick={handleLogout}
              >
                <Icon icon="lucide:log-out" width="18" height="18" />
              </button>
            {/if}
          </div>
        </div>

        <button
          type="button"
          class="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all select-none text-zinc-100/90 hover:text-white"
          aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
          onclick={() => (mobileOpen = !mobileOpen)}
        >
          <Icon icon={mobileOpen ? "lucide:x" : "lucide:menu"} width="24" height="24" />
        </button>
      </div>
    </div>
  </header>
</div>

{#if mobileOpen}
  <div class="md:hidden fixed inset-0 z-[100] flex" role="dialog" aria-label={t("nav.menu")}>
    <button type="button" class="absolute inset-0 bg-black/70 backdrop-blur-sm" aria-label={t("nav.closeMenu")} onclick={() => (mobileOpen = false)}></button>
    <div class="relative w-full max-w-sm ml-auto bg-zinc-950 border-l border-white/10 shadow-xl flex flex-col p-3">
      <div class="flex items-center justify-between mb-5">
        <div class="flex items-center gap-2 min-w-0">
          <span class="ui-logo text-semibold flex-shrink-0" aria-hidden="true">
            <Icon icon="game-icons:jet-fighter" width="16" height="16" />
          </span>
          <div class="min-w-0">
            <p class="text-base font-semibold text-white truncate">{t("app.name")}</p>
            <p class="text-xs text-zinc-500 truncate">{t("nav.menu")}</p>
          </div>
        </div>
        <button type="button" class="p-2 text-zinc-400 hover:text-white" onclick={() => (mobileOpen = false)} aria-label={t("common.close")}>
          <Icon icon="lucide:x" width="24" height="24" />
        </button>
      </div>

      <div class="flex items-center gap-2 mb-4 px-3 py-2 rounded border border-white/[0.06] bg-white/[0.02]">
        <span class={"w-2 h-2 rounded-full " + (healthy === null ? "bg-zinc-500" : healthy ? "bg-emerald-400" : "bg-rose-400")}></span>
        <span class="text-sm text-zinc-300">{healthy === null ? t("nav.checking") : healthy ? t("common.connected") : t("common.disconnected")}</span>
      </div>

      <nav class="flex flex-col gap-2">
        {#each navLinks as link}
          <button
            type="button"
            class="topnav__tab w-full justify-start text-sm py-3 px-3"
            class:is-active={isActive(link.href)}
            onclick={() => go(link.href)}
            title={link.label()}
            aria-label={link.label()}
          >
            <Icon icon={link.icon} width="16" height="16" />
            <span>{link.label()}</span>
          </button>
        {/each}
      </nav>

      <div class="mt-4 flex justify-center">
        <LanguageSwitcher />
      </div>

      {#if user}
        <button type="button" class="mt-2 w-full inline-flex items-center justify-center gap-2 rounded border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] py-2.5 text-sm text-zinc-200" onclick={() => { handleLogout(); mobileOpen = false; }}>
          <Icon icon="lucide:log-out" width="18" height="18" />
          {t("nav.logout")}
        </button>
      {/if}
    </div>
  </div>
{/if}
